<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { showLoadingToast, Tab, Tabs } from "vant";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import MessageItem from "@/components/mobile/MessageItem.vue";
import { useNotification } from "@/stores/notification";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import ThreadList, {
  ThreadListInstance,
} from "@/components/mobile/ThreadList.vue";
import { NotificationSubType } from "@/enums/notificationEnum";
import { useI18n } from "vue-i18n";

// 状态
const notificationStore = useNotification();
const { t } = useI18n();
const { readList, unreadList } = storeToRefs(notificationStore);
const currentTab = ref(0);
const isLoading = ref(false);
const readListRef = ref<ThreadListInstance | null>(null);
const unreadListRef = ref<ThreadListInstance | null>(null);
const currentFilter = computed({
  get: () =>
    currentTab.value === 0
      ? notificationStore.unreadTypeFilter
      : notificationStore.readTypeFilter,
  set: (value: string[]) => {
    if (currentTab.value === 0) {
      notificationStore.unreadTypeFilter = value;
    } else {
      notificationStore.readTypeFilter = value;
    }
  },
});

const subTypeFilterList = computed(() => {
  return Object.keys(NotificationSubType).map((key) => ({
    label: t(NotificationSubType[key].label),
    value: key,
  }));
});
// Lifecycle

onMounted(async () => {
  if (notificationStore.unreadList.length !== 0) {
    await notificationStore.initUnreadListIfAbsent(true);
  }
  await nextTick();
  unreadListRef.value?.resetScrollLoadingState();
});

watch(
  () => notificationStore.unread.changed,
  async (changed) => {
    if (changed) {
      await notificationStore.initUnreadListIfAbsent(true);
      readListRef.value?.resetScrollLoadingState();
    }
  }
);
// Event Handler
const router = useRouter();
const onUnreadClick = async (id: string) => {
  notificationStore.readNotification(id);
};
const markAllRead = async () => {
  const toast = showLoadingToast("");
  try {
    await notificationStore.readAllNotification();
    readListRef.value?.resetScrollLoadingState();
    unreadListRef.value?.resetScrollLoadingState();
  } finally {
    toast.close();
  }
};

watch(
  () => notificationStore.readTypeFilter,
  async () => {
    try {
      isLoading.value = true;
      notificationStore.resetReadData();
      await notificationStore.initReadListIfAbsent();
      await nextTick(() => readListRef.value?.resetScrollLoadingState());
    } finally {
      isLoading.value = false;
    }
  }
);
watch(
  () => notificationStore.unreadTypeFilter,
  async () => {
    try {
      isLoading.value = true;
      notificationStore.resetUnreadData();
      await notificationStore.initUnreadListIfAbsent();
      await nextTick(() => unreadListRef.value?.resetScrollLoadingState());
    } finally {
      isLoading.value = false;
    }
  }
);
</script>
<template>
  <div class="h-full grid grid-rows-[auto_auto_1fr]">
    <threads-nav :show-center="false" show-back>
      <!--      <template #right>-->
      <!--        <span v-show="isLoading" class="loading loading-ring"></span>-->
      <!--      </template>-->
    </threads-nav>
    <tabs v-model:active="currentTab" class="h-full grid grid-rows-[auto_1fr]">
      <tab :title="$t('Notifications.unread')" />
      <tab :title="$t('Notifications.read')" />
    </tabs>
    <div class="w-full h-full overflow-hidden grid grid-rows-[auto_1fr]">
      <div
        class="w-full flex border-b px-4 py-2 items-stretch gap-4 overflow-hidden"
      >
        <button
          v-if="currentTab === 0"
          class="btn btn-xs rounded h-full"
          @click="markAllRead"
        >
          {{ $t("Notifications.btn.allRead") }}
        </button>
        <SelectableTagList
          v-model:selected="currentFilter"
          :disabled="isLoading"
          :enable-multi-select="false"
          :list="subTypeFilterList"
          enable-all
          shape="square"
        />
      </div>
      <div v-if="isLoading" class="flex justify-center pt-10">
        <span class="loading"></span>
      </div>
      <thread-list
        v-else-if="currentTab === 0"
        ref="unreadListRef"
        :immediate-check="false"
        :on-pull-load="
          async () => {
            await notificationStore.initUnreadListIfAbsent(true);
            await nextTick(() => unreadListRef?.resetScrollLoadingState());
          }
        "
        :on-scroll-load="notificationStore.fetchOlderUnreadList"
      >
        <message-item
          v-for="item in unreadList"
          :key="item.id"
          :avatar-url="item.avatarUrl"
          :message="item.content"
          :name="item.title"
          :time="item.time"
          :type="item.type"
          class="cursor-pointer hover:bg-base-200"
          @click="
            item.onClick(router);
            onUnreadClick(item.id);
          "
        />
      </thread-list>
      <thread-list
        v-else-if="currentTab === 1"
        ref="readListRef"
        :on-pull-load="
          async () => {
            await notificationStore.initReadListIfAbsent(true);
            await nextTick(() => readListRef?.resetScrollLoadingState());
          }
        "
        :on-scroll-load="notificationStore.fetchOlderReadList"
      >
        <message-item
          v-for="item in readList"
          :key="item.id"
          :avatar-url="item.avatarUrl"
          :message="item.content"
          :name="item.title"
          :time="item.time"
          :type="item.type"
          class="cursor-pointer hover:bg-base-200"
          @click="item.onClick(router)"
        />
      </thread-list>
    </div>
  </div>
</template>
